// Markdown terug naar de blokken van de editor.
//
// De andere kant op van `naarMarkdown` in inhoud.ts. Nodig voor het terugleren van onze eigen
// export — en dat is meteen de enige echte test of die export compleet is: schrijf alles weg,
// lees het terug, en vergelijk. Zonder die ronde is "geen lock-in" een belofte op papier.
//
// Bewust geen markdown-pakket: we hoeven alleen te begrijpen wat we zelf schrijven, en een
// eigen versie van honderdvijftig regels is beter te overzien dan een halve CommonMark-motor
// waarvan we een kwart gebruiken. Wat we niet herkennen blijft gewoon tekst staan — dat is
// altijd beter dan het weggooien.

import { CALLOUT_SOORT, type Blok, type Merk } from "./inhoud.ts";

const KOP = /^(#{1,6})\s+(.*)$/;
const CODEHEK = /^```(.*)$/;
const STREEP = /^(-{3,}|\*{3,}|_{3,})\s*$/;
const CITAAT = /^>\s?(.*)$/;
const OPSOMMING = /^(\s*)[-*+]\s+(.*)$/;
const GENUMMERD = /^(\s*)(\d+)[.)]\s+(.*)$/;
const TAAK = /^\[([ xX])\]\s+(.*)$/;
// Een adres tussen haakjes mag zélf haakjes bevatten als die met een backslash afgeschermd
// zijn — `/wiki/Engels_\(hoofdbetekenis\)` — en er mag een titel achter staan:
// `[tekst](adres "titel")`. Beide kwamen binnen met de eerste geknipte webpagina: zonder dit
// hield de lezer op bij het eerste haakje, bleef er letterlijke markdown in de notitie staan,
// en werd van het halve adres een link die nergens heen ging.
const ADRES = String.raw`((?:\\.|[^)\s\\])+)(?:\s+"[^"]*"|\s+'[^']*')?`;
// De tekst van een link mag afgeschermde blokhaken bevatten (`[\[dode link\]](…)`) en mag leeg
// zijn (`[](…)`, wat overblijft van een plaatje dat er niet meer is). Allebei komen ze van
// Wikipedia; met `[^\]]+` bleef er letterlijke markdown in de notitie staan.
// Losse blokhaken mogen er niet in: met `[^\]]*` rekte de tekst van de ene link door tot aan de
// sluithaak van de vólgende, en verdween er een halve zin in een linktekst.
const LABEL = String.raw`((?:\\.|[^[\]\\])*)`;
const ALLEEN_AFBEELDING = new RegExp(String.raw`^!\[([^\]]*)\]\(` + ADRES + String.raw`\)$`);
const ALLEEN_LINK = new RegExp(String.raw`^\[` + LABEL + String.raw`\]\(` + ADRES + String.raw`\)$`);
const ALLEEN_BIJLAGE = /^\[([^\]]+)\]\((\/api\/bestanden\/[^)\s]+)\)$/;

// Een underscore midden in een woord maakt geen cursief — dezelfde regel als in CommonMark, en
// hier om een heel concrete reden: adressen als `/wiki/Je_maintiendrai_(hoofdbetekenis)` zitten
// er vol mee. Zonder deze regel greep het cursief van `_[Je maintiendrai](…)_` de eerste
// underscore ín het adres als eindstreep, en bleef er een halve link als letterlijke tekst in
// de notitie staan. Underscores tússen letters mogen daarom wél in de inhoud voorkomen.
const WOORD = String.raw`[\p{L}\p{N}]`;
const SCHUIN = String.raw`(?<!${WOORD})_(?:[^_]|(?<=${WOORD})_(?=${WOORD}))+_(?!${WOORD})`;

/** `\(` in een markdown-adres is gewoon een haakje; de backslash hoort niet in de link. */
function adresUitMarkdown(adres: string): string {
  return adres.replace(/\\([\\()[\]<>"'*_`])/g, "$1");
}

type Regel = { inspringen: number; tekst: string; genummerd: boolean; nummer: number };

/**
 * Een markering over alles heen leggen wat erbinnen gevonden is.
 *
 * Nodig omdat markeringen door elkaar heen lopen: `**[Verkeer & Vervoer](…)**` is vet én een
 * link. Zonder dit won de buitenste (vet) en bleef de link als letterlijke tekst in de notitie
 * staan — zichtbaar in de eerste geknipte pagina.
 */
function metMerk(blokken: Blok[], merk: Merk): Blok[] {
  return blokken.map((blok) => (blok.type === "text" ? { ...blok, marks: [...(blok.marks ?? []), merk] } : blok));
}

// Twee patronen in plaats van één, met een voorrangsregel ertussen. Verwijzingen (links en
// afbeeldingen) en opmaak (`code`, **vet**, *schuin*) kunnen door elkaar heen lopen, en dan
// wint in markdown de link — zoals in CommonMark, en om dezelfde reden: een adres zit vol
// tekens die ook opmaak kunnen zijn. Het bij elkaar in één regexp zetten zou werken, maar
// alleen met `(?:link|[^*])+`, en dat is precies de vorm waarop een reguliere expressie
// eindeloos kan gaan terugkrabbelen op een pagina die niet meewerkt.
const VERWIJZING = new RegExp(
  String.raw`(!\[[^\]]*\]\(` + ADRES + String.raw`\))|(\[` + LABEL + String.raw`\]\(` + ADRES + String.raw`\))`,
  "u"
);
const OPMAAK = new RegExp(String.raw`(\x60[^\x60]+\x60)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(` + SCHUIN + String.raw`)`, "u");

/**
 * Het eerstvolgende stuk opmaak in deze tekst.
 *
 * Loopt een link door een cursief heen — `_[Je maintiendrai](…_(hoofdbetekenis))_`, en zo staan
 * ze er echt — dan rekken we het cursief op tot voorbij de link. Lukt dat niet, dan krijgt de
 * link voorrang: liever een werkende link zonder schuine letters dan een halve link als tekst.
 */
function eerstvolgende(rest: string): { index: number; stuk: string } | null {
  const verwijzing = VERWIJZING.exec(rest);
  const opmaak = OPMAAK.exec(rest);
  if (!verwijzing) return opmaak ? { index: opmaak.index, stuk: opmaak[0] } : null;
  if (!opmaak || verwijzing.index <= opmaak.index) return { index: verwijzing.index, stuk: verwijzing[0] };

  const eindeOpmaak = opmaak.index + opmaak[0].length;
  const doorsneden = verwijzing.index < eindeOpmaak && verwijzing.index + verwijzing[0].length > eindeOpmaak;
  if (!doorsneden) return { index: opmaak.index, stuk: opmaak[0] };

  const teken = opmaak[0][0];
  const streep = opmaak[0].startsWith("**") ? "**" : teken;
  const sluit = rest.indexOf(streep, verwijzing.index + verwijzing[0].length);
  if (sluit === -1) return { index: verwijzing.index, stuk: verwijzing[0] };
  return { index: opmaak.index, stuk: rest.slice(opmaak.index, sluit + streep.length) };
}

/** Losse tekst met markeringen erin: **vet**, *schuin*, `code`, links en verwijzingen. */
export function inlineNaarBlokken(tekst: string): Blok[] {
  const uit: Blok[] = [];
  let rest = tekst;

  while (rest.length > 0) {
    const treffer = eerstvolgende(rest);
    if (!treffer) {
      if (rest) uit.push({ type: "text", text: rest });
      break;
    }
    if (treffer.index > 0) uit.push({ type: "text", text: rest.slice(0, treffer.index) });
    const stuk = treffer.stuk;

    if (stuk.startsWith("![")) {
      const [, alt, adres] = ALLEEN_AFBEELDING.exec(stuk)!;
      uit.push({ type: "image", attrs: { src: adresUitMarkdown(adres), alt } });
    } else if (stuk.startsWith("[")) {
      const [, label, rauwDoel] = ALLEEN_LINK.exec(stuk)!;
      const doel = adresUitMarkdown(rauwDoel);
      if (doel.startsWith("notitie:")) {
        uit.push({ type: "notitieLink", attrs: { id: doel.slice("notitie:".length), titel: label } });
      } else if (doel.startsWith("/api/bestanden/")) {
        uit.push({ type: "bijlage", attrs: { url: doel, naam: label, grootte: 0, mime: "" } });
      } else {
        uit.push(...metMerk(inlineNaarBlokken(label), { type: "link", attrs: { href: doel } }));
      }
    } else if (stuk.startsWith("`")) {
      // Binnen `code` geldt geen opmaak; dit is het enige stuk dat letterlijk blijft.
      uit.push({ type: "text", text: stuk.slice(1, -1), marks: [{ type: "code" }] });
    } else if (stuk.startsWith("**")) {
      uit.push(...metMerk(inlineNaarBlokken(stuk.slice(2, -2)), { type: "bold" }));
    } else {
      uit.push(...metMerk(inlineNaarBlokken(stuk.slice(1, -1)), { type: "italic" }));
    }
    rest = rest.slice(treffer.index + stuk.length);
  }

  return uit;
}

function alinea(tekst: string): Blok {
  const inhoud = inlineNaarBlokken(tekst);
  return inhoud.length > 0 ? { type: "paragraph", content: inhoud } : { type: "paragraph" };
}

/** Bouwt een lijst op uit regels die allemaal even diep of dieper inspringen. */
function lijstUitRegels(regels: Regel[], vanaf: number, diepte: number): { blok: Blok; tot: number } {
  const eerste = regels[vanaf];
  const taken = TAAK.test(eerste.tekst);
  const items: Blok[] = [];
  let i = vanaf;

  while (i < regels.length && regels[i].inspringen >= diepte) {
    const regel = regels[i];
    if (regel.inspringen > diepte) break; // hoort bij het vorige item, niet bij deze lijst
    if (TAAK.test(regel.tekst) !== taken) break; // taken en opsommingen zijn aparte lijsten
    if (regel.genummerd !== eerste.genummerd) break;

    const eigenTaak = TAAK.exec(regel.tekst);
    const tekst = eigenTaak ? eigenTaak[2] : regel.tekst;
    const kinderen: Blok[] = [alinea(tekst)];
    i++;

    // Alles wat dieper inspringt hoort bij dit item: dat is een lijst in een lijst.
    if (i < regels.length && regels[i].inspringen > diepte) {
      const onder = lijstUitRegels(regels, i, regels[i].inspringen);
      kinderen.push(onder.blok);
      i = onder.tot;
    }

    items.push(
      eigenTaak
        ? { type: "taskItem", attrs: { checked: eigenTaak[1].toLowerCase() === "x" }, content: kinderen }
        : { type: "listItem", content: kinderen }
    );
  }

  const soort = taken ? "taskList" : eerste.genummerd ? "orderedList" : "bulletList";
  const attrs = eerste.genummerd && eerste.nummer !== 1 ? { start: eerste.nummer } : undefined;
  return { blok: { type: soort, ...(attrs ? { attrs } : {}), content: items }, tot: i };
}

export function vanMarkdown(tekst: string): Blok {
  const regels = tekst.replace(/\r\n/g, "\n").split("\n");
  const blokken: Blok[] = [];
  let i = 0;

  while (i < regels.length) {
    const regel = regels[i];

    if (regel.trim() === "") {
      i++;
      continue;
    }

    const hek = CODEHEK.exec(regel);
    if (hek) {
      const taal = hek[1].trim();
      const inhoud: string[] = [];
      i++;
      while (i < regels.length && !CODEHEK.test(regels[i])) inhoud.push(regels[i++]);
      i++; // het sluithek
      blokken.push({
        type: "codeBlock",
        ...(taal ? { attrs: { language: taal } } : {}),
        content: inhoud.length > 0 ? [{ type: "text", text: inhoud.join("\n") }] : undefined
      });
      continue;
    }

    const kop = KOP.exec(regel);
    if (kop) {
      blokken.push({ type: "heading", attrs: { level: kop[1].length }, content: inlineNaarBlokken(kop[2]) });
      i++;
      continue;
    }

    if (STREEP.test(regel)) {
      blokken.push({ type: "horizontalRule" });
      i++;
      continue;
    }

    const afbeelding = ALLEEN_AFBEELDING.exec(regel.trim());
    if (afbeelding) {
      blokken.push({ type: "image", attrs: { src: adresUitMarkdown(afbeelding[2]), alt: afbeelding[1] } });
      i++;
      continue;
    }

    const bijlage = ALLEEN_BIJLAGE.exec(regel.trim());
    if (bijlage) {
      blokken.push({ type: "bijlage", attrs: { url: bijlage[2], naam: bijlage[1], grootte: 0, mime: "" } });
      i++;
      continue;
    }

    // Een pijptabel: een kopregel, een streepjesregel eronder, en daarna de rijen. Zonder die
    // streepjesregel is het geen tabel maar gewone tekst met pijpen erin.
    if (regel.trim().startsWith("|") && i + 1 < regels.length && /^\s*\|[\s:|-]+\|\s*$/.test(regels[i + 1])) {
      const cellen = (r: string): string[] =>
        r
          .trim()
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split(/(?<!\\)\|/)
          .map((c) => c.replace(/\\\|/g, "|").trim());

      const kop = cellen(regels[i]);
      i += 2;
      const rijen: Blok[] = [
        { type: "tableRow", content: kop.map((c) => ({ type: "tableHeader", content: [alinea(c)] })) }
      ];
      while (i < regels.length && regels[i].trim().startsWith("|")) {
        const waarden = cellen(regels[i]);
        rijen.push({
          type: "tableRow",
          content: kop.map((_, nr) => ({ type: "tableCell", content: [alinea(waarden[nr] ?? "")] }))
        });
        i++;
      }
      blokken.push({ type: "table", content: rijen });
      continue;
    }

    if (CITAAT.test(regel)) {
      const binnen: string[] = [];
      while (i < regels.length && CITAAT.test(regels[i])) binnen.push(CITAAT.exec(regels[i++])![1]);

      // Staat er `[!NOTE]` op de eerste regel, dan is het geen citaat maar een uitgelicht
      // blok — dezelfde vorm die GitHub gebruikt. De rest van de regels is de inhoud.
      const markering = /^\[!([A-Za-z]+)\]\s*$/.exec(binnen[0] ?? "");
      const soort = markering ? CALLOUT_SOORT[markering[1].toUpperCase()] : undefined;
      const doc = vanMarkdown((soort ? binnen.slice(1) : binnen).join("\n"));
      blokken.push(
        soort ? { type: "callout", attrs: { soort }, content: doc.content } : { type: "blockquote", content: doc.content }
      );
      continue;
    }

    if (OPSOMMING.test(regel) || GENUMMERD.test(regel)) {
      const lijstRegels: Regel[] = [];
      while (i < regels.length && (OPSOMMING.test(regels[i]) || GENUMMERD.test(regels[i]))) {
        const op = OPSOMMING.exec(regels[i]);
        const ge = GENUMMERD.exec(regels[i]);
        lijstRegels.push(
          op
            ? { inspringen: op[1].length, tekst: op[2], genummerd: false, nummer: 1 }
            : { inspringen: ge![1].length, tekst: ge![3], genummerd: true, nummer: Number(ge![2]) }
        );
        i++;
      }
      const { blok } = lijstUitRegels(lijstRegels, 0, lijstRegels[0].inspringen);
      blokken.push(blok);
      continue;
    }

    // Gewone alinea: doorlopen tot een lege regel of iets wat duidelijk een ander blok is.
    const zinnen: string[] = [];
    while (
      i < regels.length &&
      regels[i].trim() !== "" &&
      !KOP.test(regels[i]) &&
      !CODEHEK.test(regels[i]) &&
      !STREEP.test(regels[i]) &&
      !CITAAT.test(regels[i]) &&
      !OPSOMMING.test(regels[i]) &&
      !GENUMMERD.test(regels[i])
    ) {
      zinnen.push(regels[i++]);
    }
    // Twee spaties aan het eind is in markdown een harde regelovergang binnen dezelfde alinea.
    const inhoud: Blok[] = [];
    zinnen.forEach((zin, nr) => {
      inhoud.push(...inlineNaarBlokken(zin.replace(/\s+$/, "")));
      if (nr < zinnen.length - 1) inhoud.push({ type: "hardBreak" });
    });
    blokken.push(inhoud.length > 0 ? { type: "paragraph", content: inhoud } : { type: "paragraph" });
  }

  return { type: "doc", content: blokken.length > 0 ? blokken : [{ type: "paragraph" }] };
}

/** Het blokje bovenin een geëxporteerd bestand: `---`, sleutel: waarde, `---`. */
export function leesFrontMatter(tekst: string): { velden: Record<string, string>; rest: string } {
  const schoon = tekst.replace(/\r\n/g, "\n");
  if (!schoon.startsWith("---\n")) return { velden: {}, rest: schoon };
  const einde = schoon.indexOf("\n---", 3);
  if (einde < 0) return { velden: {}, rest: schoon };

  const velden: Record<string, string> = {};
  for (const regel of schoon.slice(4, einde).split("\n")) {
    const scheiding = regel.indexOf(":");
    if (scheiding < 0) continue;
    const sleutel = regel.slice(0, scheiding).trim();
    let waarde = regel.slice(scheiding + 1).trim();
    if (waarde.startsWith('"') && waarde.endsWith('"')) waarde = waarde.slice(1, -1).replace(/\\"/g, '"');
    velden[sleutel] = waarde;
  }
  return { velden, rest: schoon.slice(einde + 4).replace(/^\n+/, "") };
}
